import React, { useEffect, useMemo, useRef, useState } from "react";
import { numberWithCommas } from "../utils.js";

const CHART_VIEWS = [
  { id: "career", label: "Career" },
  { id: "turnaround", label: "Turnaround" },
  { id: "cadence", label: "Cadence" },
  { id: "payload", label: "Payload" },
  { id: "landings", label: "Landings" },
];

const LANDING_KEYS = [
  { id: "asds", label: "ASDS", color: "#238bb8" },
  { id: "rtls", label: "RTLS", color: "#168f7d" },
  { id: "catch", label: "Catch", color: "#8067d8" },
  { id: "expended", label: "Expended", color: "#c45b4a" },
  { id: "other", label: "Other", color: "#5f6979" },
];

const useElementWidth = (min = 280) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(min);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect?.width || 0);
      if (next > 0) {
        setWidth(next);
      }
    });
    observer.observe(node);
    setWidth(Math.max(min, Math.round(node.getBoundingClientRect().width || min)));
    return () => observer.disconnect();
  }, [min]);

  return [ref, width];
};

const niceTicks = (min, max, count = 4) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0, 1];
  }
  if (max <= min) {
    const base = Math.max(1, Math.ceil(max || 1));
    return Array.from({ length: count + 1 }, (_, index) => (base / count) * index);
  }

  const span = max - min;
  const raw = span / count;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const err = raw / pow;
  const step = err >= 7.5 ? 10 * pow : err >= 3.5 ? 5 * pow : err >= 1.5 ? 2 * pow : pow;
  const start = Math.floor(min / step) * step;
  const ticks = [];
  for (let value = start; value <= max + step / 2; value += step) {
    ticks.push(Number(value.toPrecision(8)));
  }
  return ticks.length ? ticks : [min, max];
};

const yearTicks = (minMs, maxMs) => {
  if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs <= minMs) {
    return [];
  }
  const start = new Date(minMs).getUTCFullYear();
  const end = new Date(maxMs).getUTCFullYear();
  const span = Math.max(1, end - start);
  const step = span > 10 ? 2 : 1;
  const ticks = [];
  for (let year = start; year <= end; year += step) {
    ticks.push({ value: Date.UTC(year, 0, 1), label: String(year) });
  }
  return ticks;
};

const formatAxisNumber = (value) => {
  if (!Number.isFinite(value)) {
    return "";
  }
  if (Math.abs(value) >= 1000) {
    const tonnes = value / 1000;
    return tonnes >= 10 ? `${Math.round(tonnes)}t` : `${tonnes.toFixed(1)}t`;
  }
  if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.05) {
    return String(Math.round(value));
  }
  return value.toFixed(1);
};

const formatTooltipDate = (ms) => {
  const date = new Date(ms);
  if (Number.isNaN(date.valueOf())) {
    return "";
  }
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const LineChart = ({
  series,
  xType = "linear",
  yLabel,
  yFormat = formatAxisNumber,
  height = 248,
  step = false,
}) => {
  const [wrapRef, width] = useElementWidth(320);
  const [hover, setHover] = useState(null);

  const pad = { top: 18, right: 14, bottom: 28, left: 42 };
  const innerW = Math.max(40, width - pad.left - pad.right);
  const innerH = height - pad.top - pad.bottom;

  const allPoints = series.flatMap((item) => item.points);
  const xValues = allPoints.map((point) => point.x).filter((value) => Number.isFinite(value));
  const yValues = allPoints.map((point) => point.y).filter((value) => Number.isFinite(value));
  const xMin = xValues.length ? Math.min(...xValues) : 0;
  const xMax = xValues.length ? Math.max(...xValues) : 1;
  const yMin = 0;
  const rawYMax = yValues.length ? Math.max(...yValues) * 1.08 : 1;
  const yTicks = niceTicks(yMin, rawYMax, 4).filter((tick) => tick >= 0);
  const yMax = Math.max(rawYMax, yTicks[yTicks.length - 1] || rawYMax);

  const xScale = (value) => pad.left + ((value - xMin) / (xMax - xMin || 1)) * innerW;
  const yScale = (value) => pad.top + innerH - ((value - yMin) / (yMax - yMin || 1)) * innerH;

  const xTicks =
    xType === "time"
      ? yearTicks(xMin, xMax)
      : niceTicks(xMin, xMax, Math.min(8, Math.max(2, Math.round(innerW / 72)))).map((value) => ({
          value,
          label: formatAxisNumber(value),
        }));

  const handleMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - bounds.left;
    const ratio = (pointerX - pad.left) / innerW;
    const xAt = xMin + Math.min(1, Math.max(0, ratio)) * (xMax - xMin || 1);

    const rows = series
      .map((item) => {
        if (!item.points.length) {
          return null;
        }
        let nearest = item.points[0];
        let best = Math.abs(nearest.x - xAt);
        item.points.forEach((point) => {
          const delta = Math.abs(point.x - xAt);
          if (delta < best) {
            best = delta;
            nearest = point;
          }
        });
        return { id: item.id, name: item.name, color: item.color, point: nearest };
      })
      .filter(Boolean);

    if (!rows.length) {
      setHover(null);
      return;
    }

    const focus = rows.reduce((best, row) =>
      Math.abs(row.point.x - xAt) < Math.abs(best.point.x - xAt) ? row : best
    );
    setHover({
      x: xScale(focus.point.x),
      label: focus.point.meta || (xType === "time" ? formatTooltipDate(focus.point.x) : `Flight ${focus.point.x}`),
      rows,
    });
  };

  const buildPath = (points) => {
    if (!points.length) {
      return "";
    }
    return points
      .map((point, index) => {
        const x = xScale(point.x);
        const y = yScale(point.y);
        if (index === 0) {
          return `M ${x} ${y}`;
        }
        if (step) {
          return `H ${x} V ${y}`;
        }
        return `L ${x} ${y}`;
      })
      .join(" ");
  };

  return (
    <div className="bc-chart-canvas" ref={wrapRef}>
      <svg
        className="bc-svg"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={yLabel || "Comparison chart"}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line
              className="bc-grid"
              x1={pad.left}
              x2={width - pad.right}
              y1={yScale(tick)}
              y2={yScale(tick)}
            />
            <text className="bc-axis-label" x={pad.left - 8} y={yScale(tick) + 3} textAnchor="end">
              {yFormat(tick)}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <text key={`x-${tick.value}`} className="bc-axis-label" x={xScale(tick.value)} y={height - 8} textAnchor="middle">
            {tick.label}
          </text>
        ))}
        {series.map((item) => (
          <path
            key={item.id}
            d={buildPath(item.points)}
            fill="none"
            stroke={item.color}
            strokeWidth="2.2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {series.map((item) => {
          const last = item.points[item.points.length - 1];
          if (!last) {
            return null;
          }
          return <circle key={`${item.id}-end`} cx={xScale(last.x)} cy={yScale(last.y)} r="3.2" fill={item.color} />;
        })}
        {hover ? (
          <line className="bc-hover-line" x1={hover.x} x2={hover.x} y1={pad.top} y2={pad.top + innerH} />
        ) : null}
      </svg>
      {hover ? (
        <div
          className="bc-tooltip"
          style={{
            left: Math.min(width - 180, Math.max(8, hover.x - 70)),
          }}
        >
          <strong>{hover.label}</strong>
          {hover.rows.map((row) => (
            <span key={row.id}>
              <i style={{ background: row.color }} />
              {row.name}
              <em>{row.point.label}</em>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const GroupedBarChart = ({ groups, series, yFormat = formatAxisNumber, height = 248 }) => {
  const [wrapRef, width] = useElementWidth(320);
  const [hover, setHover] = useState(null);
  const pad = { top: 18, right: 10, bottom: 28, left: 42 };
  const innerW = Math.max(40, width - pad.left - pad.right);
  const innerH = height - pad.top - pad.bottom;
  const maxValue = Math.max(1, ...groups.flatMap((group) => series.map((item) => Number(group.values[item.id] || 0))));
  const yTicks = niceTicks(0, maxValue * 1.08, 4);
  const yMax = Math.max(maxValue * 1.08, yTicks[yTicks.length - 1] || maxValue);
  const yScale = (value) => pad.top + innerH - (value / (yMax || 1)) * innerH;
  const groupWidth = innerW / Math.max(1, groups.length);
  const barWidth = Math.max(4, Math.min(18, (groupWidth - 12) / Math.max(1, series.length)));

  return (
    <div className="bc-chart-canvas" ref={wrapRef}>
      <svg className="bc-svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img">
        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line className="bc-grid" x1={pad.left} x2={width - pad.right} y1={yScale(tick)} y2={yScale(tick)} />
            <text className="bc-axis-label" x={pad.left - 8} y={yScale(tick) + 3} textAnchor="end">
              {yFormat(tick)}
            </text>
          </g>
        ))}
        {groups.map((group, groupIndex) => {
          const gx = pad.left + groupIndex * groupWidth;
          return (
            <g key={group.id}>
              {series.map((item, seriesIndex) => {
                const value = Number(group.values[item.id] || 0);
                const x = gx + (groupWidth - series.length * barWidth) / 2 + seriesIndex * barWidth;
                const y = yScale(value);
                const h = pad.top + innerH - y;
                return (
                  <rect
                    key={`${group.id}-${item.id}`}
                    x={x}
                    y={y}
                    width={Math.max(3, barWidth - 2)}
                    height={Math.max(0, h)}
                    fill={item.color}
                    opacity={hover && hover.key !== `${group.id}-${item.id}` ? 0.35 : 0.92}
                    onMouseEnter={() =>
                      setHover({
                        key: `${group.id}-${item.id}`,
                        x: x + barWidth / 2,
                        label: String(group.label),
                        rows: [{ id: item.id, name: item.name, color: item.color, point: { label: `${value}` } }],
                      })
                    }
                    onMouseLeave={() => setHover(null)}
                  />
                );
              })}
              <text className="bc-axis-label" x={gx + groupWidth / 2} y={height - 8} textAnchor="middle">
                {group.label}
              </text>
            </g>
          );
        })}
      </svg>
      {hover ? (
        <div className="bc-tooltip" style={{ left: Math.min(width - 180, Math.max(8, hover.x - 70)) }}>
          <strong>{hover.label}</strong>
          {hover.rows.map((row) => (
            <span key={row.id}>
              <i style={{ background: row.color }} />
              {row.name}
              <em>{row.point.label}</em>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const LandingMix = ({ items }) => {
  const [hover, setHover] = useState(null);

  return (
    <div className="bc-landing-mix">
      {items.map((item) => {
        const total = LANDING_KEYS.reduce((sum, key) => sum + Number(item.mix[key.id] || 0), 0);
        return (
          <div className="bc-landing-row" key={item.id}>
            <div className="bc-landing-label">
              <i style={{ background: item.color }} />
              <strong>{item.name}</strong>
              <span>{total} landings</span>
            </div>
            <div className="bc-landing-track" aria-label={`${item.name} landing mix`}>
              {total ? (
                LANDING_KEYS.map((key) => {
                  const count = Number(item.mix[key.id] || 0);
                  if (!count) {
                    return null;
                  }
                  return (
                    <span
                      key={key.id}
                      style={{ width: `${(count / total) * 100}%`, background: key.color }}
                      onMouseEnter={() =>
                        setHover({
                          id: `${item.id}-${key.id}`,
                          name: item.name,
                          label: key.label,
                          count,
                          pct: Math.round((count / total) * 100),
                        })
                      }
                      onMouseLeave={() => setHover(null)}
                    />
                  );
                })
              ) : (
                <span className="bc-landing-empty">No recoveries logged</span>
              )}
            </div>
            <div className="bc-landing-counts">
              {LANDING_KEYS.filter((key) => item.mix[key.id]).map((key) => (
                <em key={key.id}>
                  {key.label} {item.mix[key.id]}
                </em>
              ))}
            </div>
          </div>
        );
      })}
      {hover ? (
        <p className="bc-landing-hover">
          {hover.name}: {hover.count} {hover.label.toLowerCase()} ({hover.pct}%)
        </p>
      ) : (
        <div className="bc-landing-legend">
          {LANDING_KEYS.map((key) => (
            <span key={key.id}>
              <i style={{ background: key.color }} />
              {key.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const buildCareerSeries = (items) =>
  items
    .map((item) => ({
      id: item.id,
      name: item.profile.name,
      color: item.color,
      points: (item.profile.flightsLog || []).map((flight, index) => ({
        x: flight.time.valueOf(),
        y: index + 1,
        label: `x${index + 1}`,
        meta: flight.launchName || formatTooltipDate(flight.time.valueOf()),
      })),
    }))
    .filter((item) => item.points.length);

const buildTurnaroundSeries = (items) =>
  items
    .map((item) => ({
      id: item.id,
      name: item.profile.name,
      color: item.color,
      points: (item.profile.flightsLog || [])
        .filter((flight) => Number.isFinite(flight.turnaroundDays))
        .map((flight) => ({
          x: flight.flightNumber,
          y: flight.turnaroundDays,
          label: `${Math.round(flight.turnaroundDays)}d`,
          meta: `Flight ${flight.flightNumber}`,
        })),
    }))
    .filter((item) => item.points.length);

const buildPayloadSeries = (items) =>
  items
    .map((item) => {
      let running = 0;
      const points = (item.profile.flightsLog || [])
        .filter((flight) => Number.isFinite(flight.massKg))
        .map((flight) => {
          running += flight.massKg;
          return {
            x: flight.time.valueOf(),
            y: running / 1000,
            label: `${running >= 1000 ? numberWithCommas(Math.round(running / 1000)) : (running / 1000).toFixed(1)} t`,
            meta: flight.launchName || formatTooltipDate(flight.time.valueOf()),
          };
        });
      return { id: item.id, name: item.profile.name, color: item.color, points };
    })
    .filter((item) => item.points.length);

const buildCadenceGroups = (items) => {
  const years = new Set();
  items.forEach((item) => {
    Object.keys(item.profile.flightsByYear || {}).forEach((year) => years.add(Number(year)));
  });
  const sorted = [...years].filter(Number.isFinite).sort((a, b) => a - b);
  return sorted.map((year) => ({
    id: String(year),
    label: `'${String(year).slice(2)}`,
    values: Object.fromEntries(
      items.map((item) => [item.id, Number(item.profile.flightsByYear?.[year] || 0)])
    ),
  }));
};

const VIEW_COPY = {
  career: {
    title: "Cumulative flights",
    note: "Staircase of reuse — who pulled ahead, and when.",
  },
  turnaround: {
    title: "Turnaround by flight",
    note: "Days on the ground before each reuse. Lower is faster.",
  },
  cadence: {
    title: "Flights per year",
    note: "How hard each booster was worked in a given year.",
  },
  payload: {
    title: "Cumulative payload lofted",
    note: "Running tonnes to orbit on missions with a known mass.",
  },
  landings: {
    title: "Landing mix",
    note: "Where each booster came home — ASDS, RTLS, catch, or expended.",
  },
};

const CompareChartPanel = ({ items = [] }) => {
  const [view, setView] = useState("career");
  const ready = items.filter((item) => item.profile);
  const copy = VIEW_COPY[view];

  const careerSeries = useMemo(() => buildCareerSeries(ready), [ready]);
  const turnaroundSeries = useMemo(() => buildTurnaroundSeries(ready), [ready]);
  const payloadSeries = useMemo(() => buildPayloadSeries(ready), [ready]);
  const cadenceGroups = useMemo(() => buildCadenceGroups(ready), [ready]);
  const landingItems = useMemo(
    () =>
      ready.map((item) => ({
        id: item.id,
        name: item.profile.name,
        color: item.color,
        mix: item.profile.landingMix || {},
      })),
    [ready]
  );

  const empty =
    (view === "career" && !careerSeries.length) ||
    (view === "turnaround" && !turnaroundSeries.length) ||
    (view === "payload" && !payloadSeries.length) ||
    (view === "cadence" && !cadenceGroups.length);

  return (
    <section className="bc-charts" aria-label="Comparison graphs">
      <div className="bc-charts-head">
        <div>
          <p className="bt-section-label">Live graphs</p>
          <h3 className="bc-charts-title">{copy.title}</h3>
          <p className="bc-charts-note">{copy.note}</p>
        </div>
        <div className="bc-chart-tabs" role="tablist" aria-label="Chart type">
          {CHART_VIEWS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={view === option.id}
              className={view === option.id ? "active" : ""}
              onClick={() => setView(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bc-chart-legend">
        {ready.map((item) => (
          <span key={item.id}>
            <i style={{ background: item.color }} />
            {item.profile.name}
          </span>
        ))}
      </div>

      {empty ? (
        <div className="bc-chart-empty">
          <strong>Not enough flight history</strong>
          <span>Pick a flown booster to plot this view.</span>
        </div>
      ) : view === "career" ? (
        <LineChart series={careerSeries} xType="time" yLabel="Cumulative flights" step />
      ) : view === "turnaround" ? (
        <LineChart
          series={turnaroundSeries}
          xType="linear"
          yLabel="Turnaround days"
          yFormat={(value) => `${Math.round(value)}d`}
        />
      ) : view === "payload" ? (
        <LineChart
          series={payloadSeries}
          xType="time"
          yLabel="Tonnes lofted"
          yFormat={(value) => `${formatAxisNumber(value)}t`}
          step
        />
      ) : view === "cadence" ? (
        <GroupedBarChart
          groups={cadenceGroups}
          series={ready.map((item) => ({ id: item.id, name: item.profile.name, color: item.color }))}
        />
      ) : (
        <LandingMix items={landingItems} />
      )}
    </section>
  );
};

export default CompareChartPanel;
